import { useTranslation } from 'react-i18next';

import { Input, Label, SectionTitle, Select } from './FormComponents';
import { formatUsPhone } from '../../lib/phone';

import type { PersonalInfo } from '../../types/application';
import type { ChangeEvent } from 'react';

export type StepAErrors = Partial<Record<keyof PersonalInfo, string>>;

interface StepAPersonalInfoProps {
  value: PersonalInfo;
  errors: StepAErrors;
  onChange: (patch: Partial<PersonalInfo>) => void;
}

export function StepAPersonalInfo({ value, errors, onChange }: StepAPersonalInfoProps) {
  const { t } = useTranslation();

  const handle = (key: keyof PersonalInfo) => (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const raw = event.target.value;
    if (key === 'phone') {
      onChange({ phone: formatUsPhone(raw) });
      return;
    }
    onChange({ [key]: raw } as Partial<PersonalInfo>);
  };

  return (
    <div className='space-y-4'>
      <SectionTitle title={t('wizard.stepA.title')} subtitle={t('wizard.stepA.subtitle')} />

      <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
        <div>
          <Label required error={errors.firstName}>
            {t('wizard.stepA.firstName')}
          </Label>
          <Input
            value={value.firstName}
            onChange={handle('firstName')}
            error={errors.firstName}
            autoComplete='given-name'
          />
        </div>

        <div>
          <Label error={errors.middleName}>{t('wizard.stepA.middleName')}</Label>
          <Input
            value={value.middleName ?? ''}
            onChange={handle('middleName')}
            error={errors.middleName}
            autoComplete='additional-name'
          />
        </div>

        <div>
          <Label required error={errors.lastName}>
            {t('wizard.stepA.lastName')}
          </Label>
          <Input
            value={value.lastName}
            onChange={handle('lastName')}
            error={errors.lastName}
            autoComplete='family-name'
          />
        </div>

        <div>
          <Label error={errors.preferredName}>{t('wizard.stepA.preferredName')}</Label>
          <Input
            value={value.preferredName ?? ''}
            onChange={handle('preferredName')}
            error={errors.preferredName}
            autoComplete='nickname'
          />
        </div>

        <div>
          <Label required error={errors.email}>
            {t('wizard.stepA.email')}
          </Label>
          <Input
            type='email'
            value={value.email}
            onChange={handle('email')}
            error={errors.email}
            autoComplete='email'
          />
        </div>

        <div>
          <Label required error={errors.phone}>
            {t('wizard.stepA.phone')}
          </Label>
          <Input
            inputMode='tel'
            placeholder={t('wizard.stepA.phonePlaceholder')}
            value={value.phone}
            onChange={handle('phone')}
            error={errors.phone}
            autoComplete='tel'
          />
        </div>

        <div className='md:col-span-2 space-y-4 pt-4'>
          <div>
            <Label required error={errors.address}>
              {t('wizard.stepA.address')}
            </Label>
            <Input
              value={value.address}
              onChange={handle('address')}
              placeholder={t('wizard.stepA.addressPlaceholder')}
              error={errors.address}
              autoComplete='street-address'
            />
          </div>

          <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
            <div className='col-span-1 md:col-span-2'>
              <Input
                value={value.city}
                onChange={handle('city')}
                placeholder={t('wizard.stepA.cityPlaceholder')}
                error={errors.city}
                autoComplete='address-level2'
              />
            </div>
            <div>
              <Select value={value.state} onChange={handle('state')} error={errors.state} autoComplete='address-level1'>
                <option value=''>{t('wizard.stepA.statePlaceholder')}</option>
                <option value='FL'>{t('wizard.stepA.states.FL')}</option>
                <option value='NY'>{t('wizard.stepA.states.NY')}</option>
                <option value='TX'>{t('wizard.stepA.states.TX')}</option>
                <option value='CA'>{t('wizard.stepA.states.CA')}</option>
              </Select>
            </div>
            <div>
              <Input
                value={value.zip}
                onChange={handle('zip')}
                placeholder={t('wizard.stepA.zipPlaceholder')}
                error={errors.zip}
                autoComplete='postal-code'
              />
            </div>
          </div>
        </div>

        <div className='md:col-span-2'>
          <Label error={errors.language}>{t('wizard.stepA.language')}</Label>
          <Select value={value.language} onChange={handle('language')} error={errors.language}>
            <option value='English'>{t('wizard.stepA.languages.English')}</option>
            <option value='Spanish'>{t('wizard.stepA.languages.Spanish')}</option>
          </Select>
        </div>
      </div>
    </div>
  );
}
